<script lang="ts">
    import cn from 'classnames';
    import { onDestroy, onMount, tick } from 'svelte';
    import { throttle } from 'throttle-debounce';

    import type { StoryFeedItem } from '@api/story/types';
    import { timeoutRequest } from '@api/utils.js';
    import { Spinner } from '@components/common/spinner';
    import { Video } from '@components/common/video';
    import { storyFeedStore } from '@components/story/feed';
    import { Button, ButtonTypes } from '@uikit/button';
    import { IconName } from '@uikit/svg-icon';
    import { Size } from '@uikit/types';

    import s from './player.module.css';

    export let classes = '';
    export let story: StoryFeedItem;
    export let isBlocked = false;
    export let isCovered: boolean;

    const VIDEO_LOADING_TIMEOUT = 5000;

    let rootEl: HTMLElement;
    let videoEl: HTMLVideoElement;
    let observer: IntersectionObserver | null = null;
    let abortController: AbortController | null = null;
    let videoUrl: string | null = null;
    let isAutoplayFailed = false;
    let isLoaded = false;

    async function play() {
        if (!videoEl) {
            return;
        }

        try {
            await videoEl.play();
            isAutoplayFailed = false;
        } catch (e) {
            console.error(e);
            isAutoplayFailed = true;
            throw e;
        }
    }

    function stop() {
        if (!videoEl) {
            return;
        }

        videoEl.pause();
        videoEl.currentTime = 0.1;
    }

    async function load() {
        abortController = new AbortController();
        try {
            const request = fetch(story.videoUrl, { signal: abortController.signal });
            const response = await timeoutRequest(request, VIDEO_LOADING_TIMEOUT);
            if (!response.ok) {
                throw new Error(response.statusText);
            }

            const blob = await response.blob();
            videoUrl = URL.createObjectURL(blob);
            isLoaded = true;
        } catch (error) {
            if ((error as Error).name === 'Timeout') {
                unload();
            }

            if ((error as Error).name !== 'AbortError') {
                console.error(error);
            }

            isLoaded = false;
        }
    }

    function unload() {
        if (abortController) {
            abortController.abort();
            abortController = null;
        }

        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            videoUrl = null;
        }

        isLoaded = false;
    }

    function startPreloading() {
        observer = new IntersectionObserver(throttle(200, (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                const setState = () => entry.intersectionRatio >= 0.1 ? play() : stop();
                if (entry.isIntersecting && isLoaded) {
                    setState();
                } else if (entry.isIntersecting && !isLoaded) {
                    load()
                        .then(() => tick())
                        .then(() => setState());
                } else if (!entry.isIntersecting && isLoaded) {
                    unload();
                }
            });
        }), {
            root: null,
            rootMargin: '10% 0px',
            threshold: [0, 0.1],
        });
        observer.observe(rootEl);
    }

    function stopPreloading() {
        if (observer) {
            observer.disconnect();
            observer = null;
        }

        unload();
    }

    onMount(startPreloading);
    onDestroy(stopPreloading);
</script>

<div bind:this={rootEl} class={cn(s.root, classes)}>
    {#if !isBlocked && isLoaded}
        <Video
            wrapperClasses={s.videoWrapper}
            bind:element={videoEl}
            preload="auto"
            playsinline
            muted={$storyFeedStore.isMute}
            covered={isCovered}
            loop
            on:timeupdate
        >
            <source src={videoUrl} type="video/mp4" />
        </Video>
        {#if isAutoplayFailed}
            <Button
                classes={s.playButton}
                color={ButtonTypes.Color.Secondary}
                icon={IconName.PlaySmall}
                rounded
                size={Size.Big}
                on:click={play}
            />
        {/if}
    {:else}
        <div class={s.imgContainer}>
            <img
                class={cn(s.image, { [s.covered]: isCovered })}
                src={story.previewImgUrl}
                alt="story-preview"
            >
            {#if !isBlocked && !isLoaded}<Spinner isAbsolute></Spinner>{/if}
            {#if isBlocked}<div class={s.blurLayer} />{/if}
        </div>
    {/if}
</div>
